import { brandAndProductOf } from '@atlas/core-utils/classification'
import type { AxisSelection } from '@atlas/types'
import { AxisCode } from '@atlas/types'
import { useMemo } from 'react'
import type { ProductPage as ProductPageData } from '@/domain/explore.types'
import { useApplicableCampaigns } from '@/hooks/useCampaigns'
import { useBrandPath } from '@/hooks/useClassification'
import { useApplicableRules, useBrandKitFiles, useProductAssets, useProductMessages } from '@/hooks/useExplore'
import { useThumbnails } from '@/hooks/useThumbnails'
import { narrowToApplicable } from '@/lib/axis-applicability'

/**
 * The rules-coverage context: the product, narrowed by the geography it resolved for and by
 * the rail's own filters — so the card answers for exactly what the page below it lists.
 */
const rulesContextOf = (productCode: string | null, market: string | null, filters: AxisSelection): AxisSelection => {
	if (!productCode) return {}
	return {
		[AxisCode.BRAND]: [productCode],
		...(market ? { [AxisCode.GEOGRAPHY]: [market] } : {}),
		...filters,
	}
}

/**
 * A narrowing with the product's whole brand path stated explicitly, for the reads made
 * brand-wide — the kit, the campaigns and the texts.
 *
 * Explicitly because the server's applicability ladder covers geography only — without it, a
 * sibling product's pack shot, campaign or copy found by the brand-wide read would stay listed.
 *
 * The whole path rather than the product because brand and product are values of ONE axis,
 * and the narrowing keeps an item tagged for any value in the path: brand-wide copy is
 * tagged for the brand, not the product, and stating only the product would drop it.
 */
const withBrandPath = (brandPath: string[], narrowing: Record<string, string[]>): AxisSelection =>
	brandPath.length ? { ...narrowing, [AxisCode.BRAND]: brandPath } : narrowing

/**
 * Everything the page is currently answering for, as ONE narrowing: what applies in the
 * resolved geography, AND the rail's own filter selection. Every section is narrowed by it,
 * so no two cards on the page can be answering different questions.
 *
 * The resolved ladder wins on any axis it names: the server said which values apply there,
 * and a hand-picked value on that axis cannot overrule it.
 */
const narrowingOf = (page: ProductPageData | undefined, filters: AxisSelection): Record<string, string[]> => ({
	...filters,
	...(page?.appliesTo ?? {}),
})

/**
 * The brand's kit as this page shows it: read brand-wide, then narrowed by the page's own
 * narrowing. The kit is assets, so a rail that filters the page's content has to reach it; a
 * species filter leaving a cat-only logo on screen would contradict the grid right below it.
 *
 * The READ stays narrowing-independent — one cached result per brand — because a
 * classification filter matches a value's DESCENDANTS: filtering the request on the market
 * would drop the worldwide brandbook and the region's kit, reporting a brand with a full
 * kit as having none. Narrowing before the thumbnails also means a file that is ruled out
 * costs no presigned URL.
 */
function useApplicableBrandKit(
	page: ProductPageData | undefined,
	narrowing: Record<string, string[]>,
	isBrandPathLoading: boolean,
) {
	const kit = useBrandKitFiles(page?.brand?.code ?? null)
	// Kept referentially stable, so the card's grouping and the thumbnail queries below are
	// derived once per actual change rather than once per render.
	const files = useMemo(() => narrowToApplicable(kit.data ?? [], narrowing), [kit.data, narrowing])
	return {
		files,
		thumbnails: useThumbnails(files),
		// The brand path resolving is loading too — until it settles, `narrowing` has no
		// BRAND key at all, which the applicability filter reads as "unrestricted" rather
		// than "unknown yet" and would briefly list a sibling product's kit alongside this
		// one's.
		isLoading: kit.isLoading || isBrandPathLoading,
		isError: kit.isError,
	}
}

/**
 * Everything the page's content sections read, for one product at one resolved scope.
 *
 * Separate reads, not one composed response: rules live in rule-service and file metadata in
 * file-service, both of which already depend on classification-service (ADR-0010), so composing
 * them server-side would close a dependency cycle. Each resolves independently, so a slow
 * secondary read costs its own card a skeleton rather than blanking the page.
 *
 * Gathered in one hook so the page states the narrowing once: every section is read for it, and
 * two cards answering different questions side by side would be the bug either way.
 *
 * In its own module, not the page's, because the tabs component types its `sections` prop from
 * this hook's return: importing it from the page would close a cycle between the two files.
 */
export function useProductSections(
	page: ProductPageData | undefined,
	productCode: string | null,
	market: string | null,
	filters: AxisSelection,
) {
	// One narrowing for every section read on tags in hand — the kit and the texts. Memoized
	// because it is what the sections derive their file lists from, and a fresh object every
	// render would re-derive every one of them.
	const narrowing = useMemo(() => narrowingOf(page, filters), [page, filters])
	const { path: brandPath, isLoading: isBrandPathLoading } = useBrandPath(productCode)
	const brandWideNarrowing = useMemo(() => withBrandPath(brandPath, narrowing), [brandPath, narrowing])
	const { brandCode } = brandAndProductOf(brandPath)
	const assets = useProductAssets(productCode, market, filters)
	const texts = useProductMessages(brandCode)
	const rulesContext = rulesContextOf(productCode, market, filters)
	return {
		assets,
		thumbnails: useThumbnails(assets.data ?? []),
		brandKit: useApplicableBrandKit(page, brandWideNarrowing, isBrandPathLoading),
		// A disabled query is not loading, so until the brand resolves the card would read "no texts".
		texts: { ...texts, isLoading: texts.isLoading || isBrandPathLoading },
		applicableTexts: narrowToApplicable(texts.data ?? [], brandWideNarrowing),
		campaigns: useApplicableCampaigns(productCode, brandCode, brandWideNarrowing),
		rules: useApplicableRules(rulesContext, !!page),
		rulesContext,
	}
}
